<template>
  <layout-basic>
    <section class="buy-now-section">
      <v-container>
        <!-- Hero -->
        <div class="buy-now-header">
          <div class="price-banner">BUY IMPROVE</div>
          <h1 class="font-sora">Buy IMPROVE</h1>
          <p class="buy-now-lead">
            Choose your plan, sign your agreement online and pay securely. Your
            account is created once payment is confirmed.
          </p>
        </div>

        <v-row class="buy-now-grid">
          <!-- Wizard -->
          <v-col cols="12" md="7" lg="8" class="order-last order-md-first">
            <v-card class="buy-now-card" flat>
              <!-- Stepper header -->
              <div class="wizard-steps" aria-label="Purchase steps">
                <template v-for="(s, i) in stepList">
                  <div
                    :key="'step-' + s.n"
                    class="wizard-step"
                    :class="{ current: step === s.n, done: step > s.n }"
                  >
                    <span class="step-index">
                      <template v-if="step > s.n">✔</template>
                      <template v-else>{{ s.n }}</template>
                    </span>
                    <span class="wizard-step-label">{{ s.label }}</span>
                  </div>
                  <span
                    v-if="i < stepList.length - 1"
                    :key="'line-' + s.n"
                    class="wizard-line"
                    :class="{ done: step > s.n }"
                  ></span>
                </template>
              </div>

              <!-- Step 1: your details -->
              <div v-if="step === 1">
                <div v-if="notice" class="info-panel">
                  <p>{{ notice }}</p>
                </div>

                <div class="card-heading">
                  <span class="step-index">1</span>
                  <div>
                    <h2 class="font-sora">Your details</h2>
                    <p>Choose your plan and tell us about your business.</p>
                  </div>
                </div>

                <p class="section-label">Choose your plan</p>
                <div class="plan-tiles">
                  <button
                    v-for="p in planOptions"
                    :key="p.slug"
                    type="button"
                    class="plan-tile"
                    :class="{ selected: plan === p.slug }"
                    @click="plan = p.slug; planTouched = true"
                  >
                    <span class="plan-tile-check" aria-hidden="true">✔</span>
                    <span class="plan-tile-name">{{ p.name }}</span>
                    <span class="plan-tile-users">{{ p.users }}</span>
                    <span class="plan-tile-price">{{ p.price }}<small> year 1</small></span>
                    <span class="plan-tile-renewal">then {{ p.renewal }} a year for years 2 and 3</span>
                  </button>
                </div>

                <p class="section-label section-label-spaced">About your business</p>

                <!-- Arrived from the app with a trial account: say so, because
                     the natural worry is "will I lose what I set up". The
                     details below are prefilled from the trial and stay
                     editable; the agreement is what they sign. -->
                <div v-if="fromTrial" class="upgrade-note" role="status">
                  <strong>Upgrading your trial account.</strong>
                  This order activates the IMPROVE account you already have,
                  with the people and courses you set up during your trial.
                  You will sign your agreement and pay in the next two steps,
                  then sign in as usual.
                </div>

                <v-form ref="form" @submit.prevent="submitDetails">
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <label class="field-label">First name</label>
                      <v-text-field
                        v-model="first_name"
                        :rules="[rules.required, rules.name]"
                        placeholder="John"
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <label class="field-label">Last name</label>
                      <v-text-field
                        v-model="last_name"
                        :rules="[rules.required, rules.name]"
                        placeholder="Smith"
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <label class="field-label">Work email</label>
                      <v-text-field
                        v-model="email"
                        :rules="[rules.required, rules.email]"
                        placeholder="johnsmith@email.com"
                        type="email"
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <label class="field-label">Phone</label>
                      <v-text-field
                        v-model="phone"
                        :rules="[rules.required, rules.phone]"
                        placeholder="07123 456789"
                        type="tel"
                        maxlength="64"
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <label class="field-label">Company name</label>
                      <v-text-field
                        v-model="company_name"
                        :rules="[rules.required, rules.maxCompanyName]"
                        placeholder="Smith Inc"
                        maxlength="191"
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <label class="field-label">Company number (optional)</label>
                      <v-text-field
                        v-model="company_number"
                        :rules="[rules.maxCompanyNumber]"
                        placeholder="01234567"
                        hint="Companies House number, if you have one"
                        persistent-hint
                        maxlength="32"
                        outlined
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <label class="field-label">Registered address</label>
                      <v-textarea
                        v-model="registered_address"
                        :rules="[rules.required, rules.maxAddress]"
                        placeholder="1 High Street, Nottingham, NG1 1AA"
                        rows="3"
                        auto-grow
                        maxlength="512"
                        outlined
                      ></v-textarea>
                    </v-col>
                  </v-row>

                  <p class="form-terms">
                    By continuing you agree to our
                    <a href="/eula">Terms and Conditions</a>. For details of how we use
                    personal data please see our <a href="/privacy-policy">Privacy Policy</a>.
                  </p>

                  <v-btn
                    :loading="loading"
                    class="buy-now-submit-btn"
                    type="submit"
                    block
                    depressed
                  >
                    Continue to your agreement
                  </v-btn>
                </v-form>
              </div>

              <!-- Step 2: review and sign -->
              <div v-else>
                <div class="card-heading">
                  <span class="step-index">2</span>
                  <div>
                    <h2 class="font-sora">Review and sign</h2>
                    <p>Read your Master Service Agreement, then sign it electronically below.</p>
                  </div>
                </div>

                <div class="contract-frame-wrap">
                  <div v-if="contractLoading" class="contract-state">
                    <v-progress-circular
                      indeterminate
                      color="#08a0a8"
                      size="36"
                    ></v-progress-circular>
                    <p>Preparing your agreement</p>
                  </div>
                  <div v-else-if="contractError" class="contract-state">
                    <p>We could not load your agreement.</p>
                    <v-btn text color="#08a0a8" @click="fetchContract">Try again</v-btn>
                  </div>
                  <iframe
                    v-else
                    class="contract-frame"
                    sandbox=""
                    :srcdoc="contractHtml"
                    title="Master Service Agreement"
                  ></iframe>
                </div>

                <div v-if="!signed" class="sign-panel">
                  <p class="sign-legal">
                    This is a legally binding agreement. By typing your full name and
                    selecting Sign agreement you are signing electronically on behalf
                    of your company.
                  </p>
                  <v-form ref="signForm" @submit.prevent="signAgreement">
                    <label class="field-label">Type your full legal name</label>
                    <v-text-field
                      v-model="signer_name"
                      :rules="[rules.required, rules.name]"
                      placeholder="John Smith"
                      outlined
                    ></v-text-field>
                    <v-checkbox
                      v-model="agree"
                      :rules="[rules.mustAgree]"
                      class="sign-checkbox"
                      label="I have read the agreement and I am authorised to sign on behalf of the company named above"
                    ></v-checkbox>
                    <v-btn
                      :loading="signing"
                      class="buy-now-submit-btn"
                      type="submit"
                      block
                      depressed
                    >
                      Sign agreement
                    </v-btn>
                  </v-form>
                  <p class="back-link">
                    <a href="#" @click.prevent="backToDetails">Back to your details</a>
                  </p>
                </div>

                <div v-else class="signed-panel">
                  <div class="signed-head">
                    <span class="signed-tick" aria-hidden="true">✔</span>
                    <div>
                      <h3 class="font-sora">Agreement signed</h3>
                      <p>A copy is saved against your purchase.</p>
                    </div>
                  </div>
                  <div v-if="contractRef" class="signed-meta">
                    <p>
                      <span>Contract reference</span>
                      {{ contractRef }}
                    </p>
                    <p v-if="shaFinal">
                      <span>Document fingerprint</span>
                      <span class="sha" :title="shaFinal">{{ shaShort }}</span>
                    </p>
                  </div>
                  <p class="download-line">
                    Your copy of the signed agreement will be ready to download
                    once you have made your payment and it has been confirmed.
                  </p>

                  <!-- Steve's wording (17 Sep 2026) on what is charged when,
                       read before committing. One payment page follows
                       (Muhammad, 18 Sep 2026: "just continue to payment and
                       there we have all the options"): Stripe's hosted invoice
                       page, where the buyer pays by card or by bank transfer.
                       No card-or-invoice choice of our own in between. -->
                  <div class="payment-timing">
                    <h3 class="font-sora payment-timing__title">How payment works</h3>
                    <p>
                      Your Year 1 IMPROVE subscription and the one-off
                      {{ launchFee ? launchFee.amount : '£695' }} Launch Programme are
                      payable upfront when you start your agreement.
                    </p>
                    <p>
                      Your Year 2 and Year 3 subscription fees are then payable annually
                      in advance on or before each anniversary of your agreement.
                    </p>
                    <p>
                      On the next page you pay by card or by bank transfer. Card: your
                      account is created within minutes. Bank transfer: your account is
                      created as soon as the money arrives, and you have 30 days to pay.
                      Your invoice is emailed to you as well, so you can come back to it.
                    </p>
                  </div>

                  <div v-if="paymentsUnavailable" class="info-panel">
                    <p>
                      Online payment is being enabled. Your signed agreement is saved
                      and our team will contact you to complete payment.
                    </p>
                  </div>
                  <v-btn
                    v-else
                    :loading="checkoutLoading"
                    class="buy-now-submit-btn"
                    block
                    depressed
                    @click="goToPayment"
                  >
                    Continue to payment
                  </v-btn>
                  <p v-if="!paymentsUnavailable" class="secure-note">
                    <img src="static/icons/envelope.png" alt="" class="secure-icon" />
                    You will be taken to our secure Stripe payment page. Card details
                    are entered on Stripe, never stored by us.
                  </p>
                </div>
              </div>

            </v-card>
          </v-col>

          <!-- Order summary -->
          <v-col cols="12" md="5" lg="4" class="order-first order-md-last">
            <div class="summary-sticky">
              <v-card class="summary-card" flat>
                <div class="summary-head">
                  <span class="summary-eyebrow">YOUR PLAN</span>
                  <h3 class="summary-plan-name font-sora">
                    {{ selectedPlan ? selectedPlan.name : 'IMPROVE' }}
                  </h3>
                  <p class="summary-users">{{ selectedPlan ? selectedPlan.users : '' }}</p>
                  <div class="summary-price">
                    <span class="summary-amount">{{ selectedPlan ? selectedPlan.price : '' }}</span>
                    <span class="summary-period">year 1</span>
                  </div>
                  <p class="summary-perbiz">
                    Then {{ selectedPlan ? selectedPlan.renewal : '' }} a year for years 2 and 3.
                    Per business, billed annually, on a three year agreement.
                  </p>
                </div>

                <div class="summary-body">
                  <p class="summary-included-label">Included in every plan</p>
                  <ul class="summary-features">
                    <li v-for="f in includedFeatures" :key="f">
                      <span class="feat-check" aria-hidden="true">✔</span>{{ f }}
                    </li>
                  </ul>

                  <!-- Hidden when the catalogue loaded WITHOUT a launch fee:
                       a withdrawn fee must not be advertised. The £695 text is
                       a fallback for fetch failure only. -->
                  <div v-if="launchFee || !catalogueLoaded" class="summary-line">
                    <span>{{ launchFee ? launchFee.name : 'IMPROVE Launch Programme' }}</span>
                    <span class="summary-line-value">{{ (launchFee ? launchFee.amount : '£695') + ' one-off' }}</span>
                  </div>

                  <!-- The three year picture, so nobody signs a 36 month
                       agreement having only seen the year 1 price. -->
                  <div v-if="selectedPlan" class="summary-line summary-line--total">
                    <span>Your Total IMPROVE Investment (3 years)</span>
                    <span class="summary-line-value">{{ totalInvestment }}</span>
                  </div>

                  <!-- Steve (21 Sep 2026): dated pricing next to the figures,
                       and the reason to sign now. -->
                  <div class="summary-pricing-note">
                    <strong>October 2026 pricing</strong>
                    <p>
                      The prices shown are our current October 2026 prices. Pricing is
                      reviewed regularly and may change for new agreements.
                    </p>
                    <p>
                      Secure today's pricing by signing your 3-year agreement now. Once
                      your agreement is signed, your agreed pricing is protected for the
                      full three-year term.
                    </p>
                  </div>
                  <div class="summary-pricing-note summary-pricing-note--invoice">
                    <strong>Prefer to pay by invoice?</strong>
                    <p>
                      Choose Bank transfer on the payment page instead of paying by card.
                      Your IMPROVE trial account will be moved to live status once your
                      first year's invoice has been paid.
                    </p>
                  </div>

                  <div class="summary-foot">
                    <p class="summary-reassure">
                      Secure payment via Stripe, by card or by bank transfer. Review everything before you
                      confirm.
                    </p>
                    <p class="summary-vat">All prices exclusive of VAT. VAT is added at the checkout.</p>
                  </div>
                </div>
              </v-card>

              <p class="summary-help">
                Not sure which plan?
                <a href="/pricing">Compare plans</a> or
                <a href="/contact">talk to our team</a>.
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PurchaseAPI from '@/Api/purchase.js';
import { formatPounds, planDisplayName } from '@/utils/currency.js';

// Display FALLBACK ONLY. Stripe (via GET /public/purchase/catalogue) is the
// source of truth for plan names and prices; fetchCatalogue() replaces these
// wholesale once the catalogue loads. They are shown just while it loads or
// when the endpoint is unavailable. The slug is what the purchase API
// expects, and the amount actually charged is resolved server-side anyway.
const FALLBACK_PLANS = {
  essential: { slug: 'essential', name: 'Essential', price: '£995', renewal: '£1,695', amountPence: 99500, renewalPence: 169500, users: '1-50 users' },
  growth: { slug: 'growth', name: 'Growth', price: '£1,695', renewal: '£2,495', amountPence: 169500, renewalPence: 249500, users: '51-100 users' },
  unlimited: { slug: 'unlimited', name: 'Unlimited', price: '£2,995', renewal: '£3,995', amountPence: 299500, renewalPence: 399500, users: '101-250 users' },
};
// Years 2 and 3 prices from the September 2026 brochure, used until the
// catalogue carries renewal_amount_pence for each plan.
const RENEWAL_FALLBACK_PENCE = { essential: 169500, growth: 249500, unlimited: 399500 };
const LAUNCH_FEE_FALLBACK_PENCE = 69500;

const PURCHASE_UUID_KEY = 'improve_purchase_uuid';

export default {
  name: 'BuyNow',
  components: { LayoutBasic },
  data() {
    return {
      step: 1,
      uuid: null,
      notice: '',

      // Step 1: details
      first_name: '',
      last_name: '',
      company_name: '',
      // True when the buyer arrived from the app's Buy IMPROVE button with an
      // email prefilled: the API will upgrade their trial rather than create
      // a second account, and the page says so.
      fromTrial: false,
      registered_address: '',
      company_number: '',
      email: '',
      phone: '',
      plan: 'growth',
      refID: 'website',
      loading: false,

      // Plan tiles / summary data, keyed by slug. Starts as the hardcoded
      // fallback; replaced wholesale by fetchCatalogue() once Stripe's
      // catalogue loads.
      plans: FALLBACK_PLANS,
      // { name, amount } from the catalogue's one-off launch fee, when present.
      launchFee: null,
      catalogueLoaded: false,
      // set on first tile click; the catalogue fetch must never override a
      // choice the buyer has made
      planTouched: false,

      // Step 2: contract and signing
      contractHtml: '',
      contractLoading: false,
      contractError: false,
      signer_name: '',
      agree: false,
      signing: false,
      signed: false,
      contractRef: '',
      shaFinal: '',

      // Step 3: payment
      checkoutLoading: false,
      paymentsUnavailable: false,
      // 'card' (Stripe hosted checkout, the default) or 'invoice' (Stripe
      // issues an invoice with 30 day terms; account on payment).


      includedFeatures: [
        'Learning Management System',
        'Unlimited Courses',
        'Unlimited Playlists',
        'Reporting & Dashboards',
        'Certificates',
        'Mobile Friendly Access',
        'User Management',
        'Email Support',
      ],
      rules: {
        required: (value) => !!value || 'Required.',
        email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Invalid email.',
        // Digits, spaces, +, brackets and dashes; at least 7 digits (UK numbers
        // are 10 or 11, international with a country code up to 15).
        phone: (value) => {
          const digits = String(value || '').replace(/\D/g, '');
          return (/^[\d\s+()-]+$/.test(String(value || '').trim()) && digits.length >= 7 && digits.length <= 15) || 'Enter a valid phone number.';
        },
        name: (value) => (value && value.length >= 2) || 'Name must be at least 2 characters.',
        maxCompanyName: (value) =>
          !value || value.length <= 191 || 'Company name must be 191 characters or fewer.',
        maxAddress: (value) =>
          !value || value.length <= 512 || 'Registered address must be 512 characters or fewer.',
        maxCompanyNumber: (value) =>
          !value || value.length <= 32 || 'Company number must be 32 characters or fewer.',
        mustAgree: (value) => !!value || 'Please confirm before signing.',
      },
    };
  },
  computed: {
    // Year 1 + two years at the years 2 and 3 price + the launch fee (the
    // catalogue's if loaded, the fallback if the fetch failed, nothing if the
    // fee has been withdrawn).
    totalInvestment() {
      const p = this.selectedPlan;
      if (!p) return '';
      const fee = this.launchFee
        ? this.launchFee.amountPence
        : this.catalogueLoaded ? 0 : LAUNCH_FEE_FALLBACK_PENCE;
      return formatPounds(p.amountPence + 2 * p.renewalPence + fee);
    },
    planOptions() {
      return Object.values(this.plans);
    },
    selectedPlan() {
      return this.plans[this.plan] || null;
    },
    stepList() {
      return [
        { n: 1, label: 'Your details' },
        { n: 2, label: 'Review and sign' },
        { n: 3, label: 'Payment' },
      ];
    },
    shaShort() {
      return this.shaFinal ? this.shaFinal.slice(0, 16) + '…' : '';
    },
  },
  created() {
    const q = (this.$route.query.plan || '').toLowerCase();
    if (this.plans[q]) this.plan = q;
    this.fetchCatalogue(q);
    this.prefillFromQuery();
  },
  mounted() {
    // Resume an in-flight purchase if one is stored for this browser session.
    const stored = PurchaseAPI.storageGet(PURCHASE_UUID_KEY);
    if (!stored) return;
    PurchaseAPI.status(stored)
      .then((res) => {
        const data = res && res.data ? res.data : {};
        if (data.status === 'started') {
          this.uuid = stored;
          this.goToContract();
        } else if (data.status === 'contract_signed' || data.status === 'payment_pending') {
          // payment_pending = a Stripe session was opened but not completed
          // (browser Back from Stripe, abandoned tab). The contract is already
          // signed, so land on the payment step; checkout resumes server-side.
          this.uuid = stored;
          this.signed = true;
          this.step = 2;
        } else if (data.status === 'expired') {
          this.clearPurchase();
          this.notice =
            'Your previous session expired, so we have started fresh. Please enter your details again.';
        } else {
          // provisioned, or anything unexpected: start a fresh purchase.
          this.clearPurchase();
        }
      })
      .catch(() => {
        this.clearPurchase();
      });
  },
  metaInfo() {
    const url = 'https://www.improve.online/get-started';
    const title = 'Buy IMPROVE: Simple, Transparent Pricing for SMEs';
    const description =
      'Buy IMPROVE online. Choose Essential, Growth or Unlimited, sign your three year agreement online and pay securely.';
    return {
      title,
      meta: [
        { name: 'description', content: description },
        // /get-started and /buy-now render this same component. This page is
        // the front door, so it declares itself canonical and shares with a
        // real title rather than the empty one it carried until 2026-09-11.
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: url },
        { property: 'og:type', content: 'website' },
        { property: 'og:image', content: 'https://www.improve.online/static/SEO.png' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: 'https://www.improve.online/static/SEO.png' },
      ],
      link: [{ rel: 'canonical', href: url }],
    };
  },
  methods: {
    /**
     * The app's Buy IMPROVE button sends its trial admin here with their
     * details in the query string so they are not retyped. Only the fields
     * the app knows are filled; everything stays editable. `email` present
     * is the signal that this is a trial converting.
     */
    prefillFromQuery() {
      const q = this.$route.query || {};
      const take = (k) => {
        const v = q[k];
        return typeof v === 'string' ? v.trim().slice(0, 191) : '';
      };
      const email = take('email');
      if (email) {
        this.email = email;
        this.fromTrial = true;
      }
      if (take('first')) this.first_name = take('first');
      if (take('last')) this.last_name = take('last');
      if (take('company')) this.company_name = take('company');
    },
    apiErrorMessage(err) {
      const data = err && err.response ? err.response.data : null;
      let msg = 'Something went wrong. Please check your details and try again.';
      if (data) {
        if (data.errors) {
          const first = Object.keys(data.errors)[0];
          msg = Array.isArray(data.errors[first]) ? data.errors[first][0] : data.errors[first];
        } else if (data.message) {
          msg = data.message;
        }
      }
      return msg;
    },
    clearPurchase() {
      PurchaseAPI.storageRemove(PURCHASE_UUID_KEY);
      this.uuid = null;
      this.step = 1;
      this.contractHtml = '';
      this.contractLoading = false;
      this.contractError = false;
      this.signer_name = '';
      this.agree = false;
      this.signing = false;
      this.signed = false;
      this.contractRef = '';
      this.shaFinal = '';
      this.checkoutLoading = false;
      this.paymentsUnavailable = false;
    },
    resetExpired() {
      this.clearPurchase();
      this.notice =
        'Your purchase session expired, so we need to start again. It only takes a minute.';
      window.scrollTo(0, 0);
    },

    // ---- Plan catalogue ----
    // Stripe is the source of truth for plan tiles and prices. On any
    // failure we silently keep FALLBACK_PLANS: buyers should never see an
    // error toast for this, and the amount actually charged is resolved
    // server-side at checkout regardless of what was displayed.
    fetchCatalogue(preselect) {
      PurchaseAPI.catalogue()
        .then((res) => {
          const data = res && res.data ? res.data : {};
          const list = Array.isArray(data.plans) ? data.plans : [];
          const plans = {};
          list.forEach((p) => {
            if (!p || !p.slug || typeof p.amount_pence !== 'number') return;
            const renewalPence = typeof p.renewal_amount_pence === 'number'
              ? p.renewal_amount_pence
              : RENEWAL_FALLBACK_PENCE[p.slug] || p.amount_pence;
            plans[p.slug] = {
              slug: p.slug,
              name: planDisplayName(p.name),
              price: formatPounds(p.amount_pence),
              renewal: formatPounds(renewalPence),
              amountPence: p.amount_pence,
              renewalPence,
              users: p.users_label || '',
            };
          });
          if (!Object.keys(plans).length) return; // malformed payload: keep the fallback

          // Every plan must carry its years 2 and 3 price. A 200 without one
          // is an API that predates the three year agreement: printing the
          // brochure's renewal figures beside it would advertise a deal that
          // API will neither contract nor charge. Keep the fallback whole.
          if (Object.keys(plans).some((k) => typeof plans[k].renewalPence !== 'number')) return;
          // Assign the whole object so tiles and summary re-render reactively.
          this.plans = plans;
          this.catalogueLoaded = true;
          if (data.launch_fee && typeof data.launch_fee.amount_pence === 'number') {
            this.launchFee = {
              name: data.launch_fee.name || 'IMPROVE Launch Programme',
              amount: formatPounds(data.launch_fee.amount_pence),
              amountPence: data.launch_fee.amount_pence,
            };
          } else {
            // Catalogue loaded WITHOUT a launch fee: the fee has been withdrawn
            // in Stripe, so stop advertising it (the £695 text is a fallback for
            // fetch FAILURE only).
            this.launchFee = null;
          }
          // Re-apply the ?plan= preselect against the fetched slugs — but never
          // fight the buyer: once they have clicked a tile, or once a purchase
          // is in flight (resumed session past step 1), the selection and the
          // summary must stay exactly what will be contracted and charged.
          if (this.planTouched || this.step !== 1 || this.uuid) {
            return;
          }
          if (preselect && plans[preselect]) {
            this.plan = preselect;
          } else if (!plans[this.plan]) {
            this.plan = Object.keys(plans)[0];
          }
        })
        .catch(() => {
          // Endpoint unavailable (e.g. 503): keep the hardcoded fallback.
        });
    },

    // ---- Step 1 ----
    submitDetails() {
      if (this.loading) return;
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      try {
        if (localStorage.getItem('refID')) {
          this.refID = localStorage.getItem('refID');
        }
      } catch (e) { /* storage blocked; keep the default */ }
      PurchaseAPI.start({
        plan: this.plan,
        company_name: this.company_name,
        registered_address: this.registered_address,
        company_number: this.company_number ? this.company_number : undefined,
        contact_name: (this.first_name + ' ' + this.last_name).trim(),
        contact_email: this.email,
        first_name: this.first_name,
        last_name: this.last_name,
        phone: this.phone.trim(),
        refid: this.refID,
      })
        .then((res) => {
          this.loading = false;
          const data = res && res.data ? res.data : {};
          if (data.uuid) {
            this.uuid = data.uuid;
            PurchaseAPI.storageSet(PURCHASE_UUID_KEY, data.uuid);
            this.notice = '';
            this.signed = false;
            this.goToContract();
          } else {
            this.$snotify.error('Could not start your purchase. Please try again.');
          }
        })
        .catch((err) => {
          this.loading = false;
          this.$snotify.error(this.apiErrorMessage(err));
        });
    },

    // ---- Step 2 ----
    goToContract() {
      this.step = 2;
      window.scrollTo(0, 0);
      this.fetchContract();
    },
    fetchContract() {
      if (!this.uuid) return;
      this.contractLoading = true;
      this.contractError = false;
      this.contractHtml = '';
      PurchaseAPI.contract(this.uuid)
        .then((res) => {
          this.contractLoading = false;
          this.contractHtml = res && res.data ? res.data.html : '';
        })
        .catch((err) => {
          this.contractLoading = false;
          if (err && err.response && err.response.status === 410) {
            this.resetExpired();
            return;
          }
          this.contractError = true;
          this.$snotify.error(this.apiErrorMessage(err));
        });
    },
    backToDetails() {
      // Details are frozen once signed; the link is only rendered before signing.
      if (this.signed) return;
      this.step = 1;
      window.scrollTo(0, 0);
    },
    signAgreement() {
      if (this.signing) return;
      if (!this.$refs.signForm.validate()) return;
      this.signing = true;
      PurchaseAPI.sign(this.uuid, this.signer_name.trim())
        .then((res) => {
          this.signing = false;
          const data = res && res.data ? res.data : {};
          this.signed = true;
          this.contractRef = data.contract_uuid || '';
          // The api now returns the fingerprint of the document as reviewed
          // (sha256_unsigned); the stored pdf is only created after payment.
          this.shaFinal = data.sha256_unsigned || '';
        })
        .catch((err) => {
          this.signing = false;
          const status = err && err.response ? err.response.status : null;
          if (status === 409) {
            // Already signed on the server; carry straight on to payment.
            this.signed = true;
            this.goToPayment();
            return;
          }
          if (status === 410) {
            this.resetExpired();
            return;
          }
          this.$snotify.error(this.apiErrorMessage(err));
        });
    },

    // ---- Payment: one page, on Stripe ----
    // Creates the Stripe customer, the three year schedule and the year 1 +
    // Launch Programme invoice (POST /public/purchase/{uuid}/invoice), then
    // sends the buyer to Stripe's hosted invoice page, where they pay by
    // card or by bank transfer. Card pays on the spot; a bank transfer is
    // matched by Stripe when it lands. Either way invoice.paid provisions the
    // account. The invoice email carries the same link and the order page, so
    // a buyer who leaves can come back and pay within 30 days.
    goToPayment() {
      if (this.checkoutLoading) return;
      this.checkoutLoading = true;
      PurchaseAPI.invoice(this.uuid)
        .then((res) => {
          const data = res && res.data ? res.data : {};
          if (data.status === 'invoice_sent' && data.invoice_url) {
            window.location.href = data.invoice_url;
          } else if (data.status === 'invoice_sent') {
            this.$router.push({ path: '/get-started/success', query: { purchase: this.uuid } });
          } else {
            this.checkoutLoading = false;
            this.$snotify.error('Could not start your payment. Please try again.');
          }
        })
        .catch((err) => {
          this.checkoutLoading = false;
          const status = err && err.response ? err.response.status : null;
          if (status === 503) {
            // Payments not switched on yet. Not an error: the signed agreement
            // is saved and the team follows up manually.
            this.paymentsUnavailable = true;
            return;
          }
          if (status === 410) {
            this.resetExpired();
            return;
          }
          this.$snotify.error(this.apiErrorMessage(err));
        });
    },
  },
};
</script>

<style scoped>
.buy-now-section {
  --navy: var(--brand-navy, #002458);
  --teal: var(--brand-teal, #08a0a8);
  --grey: var(--brand-grey, #6b7280);
  --line: #e6ebf1;
  --tint: #eef8f8;
  background: #f7f9fb;
  padding: 56px 0 90px;
}

/* Hero */
.buy-now-header {
  text-align: center;
  max-width: 640px;
  margin: 0 auto 40px;
}

.buy-now-header .price-banner {
  width: 120px;
  height: 22px;
  background-color: var(--teal);
  color: #ffffff;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 25px;
  margin: 0 auto 18px;
}

.buy-now-header h1 {
  font-size: 42px;
  line-height: 1.15;
  font-weight: 700;
  color: var(--navy);
  margin-bottom: 14px;
}

.buy-now-lead {
  font-size: 18px;
  color: var(--grey);
  margin: 0;
}

/* Form card */
.buy-now-card {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 30px 30px 34px;
  box-shadow: 0 12px 40px rgba(0, 36, 88, 0.06);
}

/* Step circles (shared by the stepper header and card headings) */
.step-index {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--tint);
  color: var(--teal);
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Stepper header */
.wizard-steps {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 26px;
}

.wizard-step {
  display: flex;
  align-items: center;
  gap: 9px;
}

.wizard-step .step-index {
  width: 28px;
  height: 28px;
  font-size: 14px;
  background: #eef1f5;
  color: var(--grey);
  transition: background 0.15s ease, color 0.15s ease;
}

.wizard-step.current .step-index {
  background: var(--teal);
  color: #ffffff;
}

.wizard-step.done .step-index {
  background: var(--tint);
  color: var(--teal);
}

.wizard-step-label {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--grey);
  white-space: nowrap;
}

.wizard-step.current .wizard-step-label,
.wizard-step.done .wizard-step-label {
  color: var(--navy);
}

.wizard-line {
  flex: 1 1 24px;
  height: 2px;
  border-radius: 2px;
  background: var(--line);
}

.wizard-line.done {
  background: var(--teal);
}

/* Card headings */
.card-heading {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 18px;
}

.card-heading h2 {
  font-size: 21px;
  font-weight: 700;
  color: var(--navy);
  line-height: 1.2;
  margin: 2px 0 2px;
}

.card-heading p {
  font-size: 14px;
  color: var(--grey);
  margin: 0;
}

/* Section labels within a step */
.section-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--grey);
  margin: 0 0 12px;
}

.section-label-spaced {
  margin-top: 26px;
}

/* Notice / info panel */
.info-panel {
  border: 1px solid rgba(8, 160, 168, 0.35);
  background: var(--tint);
  border-radius: 10px;
  padding: 14px 16px;
  margin: 0 0 18px;
}

.info-panel p {
  font-size: 14px;
  color: var(--navy);
  margin: 0;
}

/* Plan tiles */
.plan-tiles {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.plan-tile {
  position: relative;
  text-align: left;
  background: #ffffff;
  border: 1.5px solid var(--line);
  border-radius: 12px;
  padding: 16px 16px 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  display: flex;
  flex-direction: column;
}

.plan-tile:hover {
  border-color: var(--teal);
}

.plan-tile:focus {
  outline: none;
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(8, 160, 168, 0.18);
}

.plan-tile.selected {
  border-color: var(--navy);
  background: #fbfdff;
  box-shadow: 0 6px 18px rgba(0, 36, 88, 0.1);
}

.plan-tile-check {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--navy);
  color: #ffffff;
  font-size: 11px;
  line-height: 20px;
  text-align: center;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.plan-tile.selected .plan-tile-check {
  opacity: 1;
  transform: scale(1);
}

.plan-tile-name {
  font-weight: 700;
  font-size: 16px;
  color: var(--navy);
}

.plan-tile-users {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--teal);
  margin: 3px 0 10px;
}

.plan-tile-price {
  font-size: 22px;
  font-weight: 700;
  color: var(--navy);
  margin-top: auto;
}

.plan-tile-renewal {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: var(--brand-grey, #6b7280);
}

.summary-line--total {
  margin-top: 8px;
  padding-top: 10px;
  border-top: 2px solid var(--brand-teal, #08a0a8);
  font-weight: 800;
  color: var(--brand-navy, #002458);
}

.plan-tile-price small {
  font-size: 12px;
  font-weight: 600;
  color: var(--grey);
}

/* Fields */
.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--navy);
  margin: 6px 0 6px;
}

.buy-now-card >>> .v-text-field--outlined fieldset {
  border-color: var(--line);
}

.buy-now-card >>> .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--teal);
}

.form-terms {
  font-size: 13px;
  color: var(--grey);
  margin: 18px 0 20px;
}

.form-terms a {
  color: var(--teal);
  text-decoration: none;
  font-weight: 600;
}

.upgrade-note {
  margin: 0 0 18px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #e6f6f7;
  border: 1px solid #b8e4e7;
  color: var(--navy, #002458);
  font-size: 14px;
  line-height: 1.5;
}

.upgrade-note strong {
  display: block;
  margin-bottom: 2px;
}

.buy-now-submit-btn {
  background-color: var(--navy) !important;
  color: #ffffff !important;
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 16px !important;
  letter-spacing: 0.01em !important;
  border-radius: 8px !important;
  height: 52px !important;
}

.buy-now-submit-btn:hover {
  background-color: var(--teal) !important;
}

.secure-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  color: var(--grey);
  margin: 16px 0 0;
}

.secure-icon {
  width: 15px;
  margin-top: 2px;
  opacity: 0.7;
}

/* Contract viewer */
.contract-frame-wrap {
  height: 560px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
  margin-bottom: 22px;
}

.contract-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: #ffffff;
}

.contract-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--grey);
  font-size: 14px;
}

.contract-state p {
  margin: 0;
}

/* Signing panel */
.sign-panel {
  border-top: 1px solid var(--line);
  padding-top: 20px;
}

.sign-legal {
  font-size: 13.5px;
  color: #2c3a4b;
  margin: 0 0 14px;
}

.sign-checkbox {
  margin: 12px 0 18px;
}

.sign-checkbox >>> .v-label {
  font-size: 13.5px;
  color: #2c3a4b;
}

/* custom.scss:2930 paints every checked fa checkbox WHITE with !important
   (added for a checkbox on a dark panel). On this white card the tick
   vanished when checked. This selector out-specifies it (7 classes vs 6)
   so the tick shows in brand teal. */
.sign-checkbox >>> i.v-icon.notranslate.fas.fa-check-square.theme--light.accent--text {
  color: #08a0a8 !important;
}

.back-link {
  text-align: center;
  font-size: 13.5px;
  margin: 14px 0 0;
}

.download-line {
  margin: 0 0 14px;
  font-size: 13.5px;
}

.download-line a,
.next-steps a {
  color: #08a0a8;
  font-weight: 600;
  text-decoration: underline;
}

.back-link a {
  color: var(--grey);
  text-decoration: underline;
}

/* Signed confirmation */
.signed-panel {
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fbfdff;
  padding: 20px;
}

.signed-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.signed-tick {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--teal);
  color: #ffffff;
  font-size: 15px;
  line-height: 34px;
  text-align: center;
}

.signed-head h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--navy);
  margin: 0 0 2px;
}

.signed-head p {
  font-size: 13.5px;
  color: var(--grey);
  margin: 0;
}

.signed-meta {
  border-top: 1px dashed var(--line);
  padding: 12px 0 16px;
  margin-top: 4px;
}

.signed-meta p {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: var(--navy);
  margin: 0 0 6px;
  word-break: break-all;
}

.signed-meta p span:first-child {
  flex: 0 0 auto;
  color: var(--grey);
  font-weight: 600;
  word-break: normal;
}

.sha {
  font-family: monospace;
  font-size: 12px;
}

/* Payment step */
.next-steps {
  list-style: none;
  padding: 0;
  margin: 0 0 22px;
}

@media (max-width: 599px) {
  }

.payment-timing {
  margin: 0 0 22px;
  padding: 16px 18px;
  background: #eef8f8;
  border: 1px solid #b8e4e7;
  border-radius: 10px;
}

.payment-timing__title {
  font-size: 17px;
  font-weight: 700;
  color: var(--navy, #002458);
  margin: 0 0 8px;
}

.payment-timing p {
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--navy, #002458);
}

.payment-timing p:last-child {
  margin-bottom: 0;
}

.next-steps li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14.5px;
  color: #2c3a4b;
  padding: 6px 0;
}

/* Order summary */
.summary-sticky {
  position: sticky;
  top: 90px;
}

.summary-card {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--line);
  box-shadow: 0 12px 40px rgba(0, 36, 88, 0.08);
}

.summary-head {
  background: linear-gradient(135deg, #002458 0%, #0b3a74 100%);
  color: #ffffff;
  padding: 26px 26px 24px;
}

.summary-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 4px;
  color: #7fd6db;
}

.summary-plan-name {
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 8px 0 2px;
}

.summary-users {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a9c2e6;
  margin: 0 0 14px;
}

.summary-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.summary-amount {
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  color: #ffffff;
}

.summary-period {
  font-size: 15px;
  font-weight: 600;
  color: #a9c2e6;
}

.summary-perbiz {
  font-size: 13px;
  color: #cbd9ee;
  margin: 8px 0 0;
}

.summary-body {
  background: #ffffff;
  padding: 22px 26px 24px;
}

.summary-included-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--grey);
  margin: 0 0 14px;
}

.summary-features {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
}

.summary-features li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14.5px;
  color: #2c3a4b;
  padding: 6px 0;
}

.feat-check {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--tint);
  color: var(--teal);
  font-size: 10px;
  line-height: 18px;
  text-align: center;
  margin-top: 1px;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-top: 1px dashed var(--line);
  font-size: 14.5px;
  color: var(--navy);
  font-weight: 600;
}

.summary-line-value {
  color: var(--teal);
  font-weight: 700;
}

.summary-foot {
  border-top: 1px solid var(--line);
  padding-top: 16px;
}

.summary-reassure {
  font-size: 13.5px;
  color: #2c3a4b;
  margin: 0 0 6px;
}

.summary-vat {
  font-size: 12px;
  color: var(--grey);
  margin: 0;
}

.summary-pricing-note {
  margin: 14px 0 0;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f5f8fb;
  font-size: 13.5px;
  line-height: 1.5;
  color: #42505e;
}

.summary-pricing-note strong {
  display: block;
  margin-bottom: 4px;
  color: var(--brand-navy, #002458);
}

.summary-pricing-note p {
  margin: 0 0 6px;
}

.summary-pricing-note p:last-child {
  margin-bottom: 0;
}

.summary-help {
  text-align: center;
  font-size: 13.5px;
  color: var(--grey);
  margin: 16px 0 0;
}

.summary-help a {
  color: var(--teal);
  text-decoration: none;
  font-weight: 600;
}

/* Responsive */
@media screen and (max-width: 960px) {
  .summary-sticky {
    position: static;
    margin-bottom: 24px;
  }
}

@media screen and (max-width: 600px) {
  .buy-now-section {
    padding: 36px 0 70px;
  }

  .buy-now-header h1 {
    font-size: 30px;
  }

  .buy-now-lead {
    font-size: 15px;
  }

  .buy-now-card {
    padding: 22px 18px 26px;
  }

  .wizard-step:not(.current) .wizard-step-label {
    display: none;
  }

  .contract-frame-wrap {
    height: 440px;
  }

  .plan-tiles {
    grid-template-columns: 1fr;
  }

  .plan-tile {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 14px;
    padding: 14px 16px;
  }

  .plan-tile-users {
    margin: 0;
    order: 3;
    flex-basis: 100%;
  }

  .plan-tile-price {
    margin-left: auto;
  }
}
</style>
